import { Typography } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { IconProp } from "@fortawesome/fontawesome-svg-core"

export enum EmptyContentVariant {
    CENTERED = "centered",
    SINGLE_LEFT = "single-left",
}

type StyleProps = Readonly<{
    iconBgColor: string | undefined
    iconColor: string
    isLeftAligned: boolean
}>

const useStyles = makeStyles<StyleProps>()((theme, { iconBgColor, iconColor, isLeftAligned }) => ({
    noContentContainer: {
        height: isLeftAligned ? undefined : "100%",
        display: "flex",
        flexDirection: "column",
        flexWrap: "nowrap",
        justifyContent: isLeftAligned ? "flex-start" : "center",
        alignItems: isLeftAligned ? "flex-start" : "center",
    },
    noContent: {
        width: isLeftAligned ? "100%" : "80%",
        display: "flex",
        flexDirection: "column",
        alignItems: isLeftAligned ? "flex-start" : "center",
        gap: isLeftAligned ? ".25rem" : "2rem",
    },
    noContentIcon: {
        fontSize: "2rem",
        width: "4rem",
        height: "4rem",
        borderRadius: "90px",
        display: "inline-flex",
        justifyContent: "center",
        alignItems: "center",
        color: iconColor,
        backgroundColor: iconBgColor,
    },
    noContentText: {
        display: "flex",
        flexDirection: "column",
        flexWrap: "nowrap",
        alignItems: isLeftAligned ? "flex-start" : "center",
        gap: ".5rem",
    },
    noContentHeader: {
        fontWeight: isLeftAligned ? 400 : 500,
        fontSize: isLeftAligned ? ".875rem" : "1rem",
        color: isLeftAligned ? theme.palette.tailwind.slate[400] : undefined,
    },
}))

export type EmptyContentProps = Readonly<{
    icon?: IconProp
    backgroundColor?: string
    iconColor?: string
    title?: string
    description?: string
    variant?: EmptyContentVariant
}>

export function EmptyContent({
    icon,
    backgroundColor,
    iconColor = "white",
    title,
    description,
    variant = EmptyContentVariant.CENTERED,
}: EmptyContentProps) {
    const isLeftAligned = variant === EmptyContentVariant.SINGLE_LEFT
    const { classes } = useStyles({ iconBgColor: backgroundColor, iconColor, isLeftAligned })
    const textAlign = isLeftAligned ? "left" : "center"

    return (
        <div className={classes.noContentContainer}>
            <div className={classes.noContent}>
                {icon && (
                    <div className={classes.noContentIcon}>
                        <FontAwesomeIcon icon={icon} />
                    </div>
                )}
                <div className={classes.noContentText}>
                    {title && (
                        <Typography variant="body1" align={textAlign} className={classes.noContentHeader}>
                            {title}
                        </Typography>
                    )}
                    {description && (
                        <Typography variant="body1" color="textSecondary" align={textAlign}>
                            {description}
                        </Typography>
                    )}
                </div>
            </div>
        </div>
    )
}
